import * as React from 'react';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { darken } from 'polished';

import t from './locales';
import { colors } from '../styles/variables';
import CountDown from './CountDown';
import { getImage } from '../utils/pathHelper';
import presentIcon from './images/present.svg';

import trackElementTapped from '../trackings/trackElementTapped';
import siteCookie from '../utils/siteCookie';

const TimeSaleView = styled.a`
  transition: right 1.0s ease-in-out, background-color 0.2s ease-in-out;
  position: fixed;
  top: 80px;
  z-index: 1050;
  display: flex;
  align-items: center;
  padding: 8px 24px 8px 4px;
  color: #fff;
  background-color: ${colors.red};
  border-radius: 10px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.3);
  &:hover {
    color: #fff;
    text-decoration: none;
    background-color: ${darken(0.1, colors.red)};
  }
  & > .icon {
    width: 72px;
    height: 72px;
  }
  .heading {
    font-size: 1.0em;
    margin-bottom: 0;
  }
  .caption {
    font-size: 0.7em;
    opacity: 0.8;
    margin-bottom: 0;
  }
`;

const TimeSaleClose = styled.div`
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  line-height: 16px;
  font-size: 16px;
  font-weight: bold;
  position: absolute;
  padding: 8px 10px;
  border-radius: 9px;
  right: 2px;
  top: 2px;
  background-color: rgba(255,255,255,0.1);
  &:after {
    content: "\00d7"
  }
  &:hover {
    opacity: 0.8;
  }
`;

const CountDownView = styled.div`
  font-size: 0.7em;
  b {
    font-size: 1.1em;
    padding: 0 2px;
  }
`;

interface ITimeSale {
  triggerElementId: string;
  trackingName?: string;
  discountAmount: string;
  cutOffString: string;
  timeout?: number;
}


const TimeSale: React.FC<ITimeSale> = ({ triggerElementId, trackingName, discountAmount, cutOffString, timeout }) => {

  const COOKIE_NAME = 'SNOW_SIGN_UP_COUPON_BANNER';
  const RIGHT_POSITION = '20px';
  const DEFAULT_RIGHT_POSITION = '-200%';

  const [right, setRight] = useState(DEFAULT_RIGHT_POSITION);
  const [isLoading, setLoading] = useState(false);
  const [observer, setObserver] = useState(null);
  const [timer, setTimer] = useState(null);

  function observerCallback(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry: IntersectionObserverEntry, i: number) => {
      if (!entry.isIntersecting) { return; }
      setRight(RIGHT_POSITION);
    });
  }

  function handleClick(event: React.MouseEvent) {
    event.preventDefault();
    trackElementTapped(event.currentTarget);
    setLoading(true);
    window.location.href = '/snow/auth/registration/new';
  }

  function handleClose(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setRight(DEFAULT_RIGHT_POSITION);
    clear();
    siteCookie.set('SNOW_SIGN_UP_COUPON_BANNER', '1', { expires: 3 });
  }

  function clear() {
    const trigger = document.getElementById(triggerElementId);
    if (observer && trigger) {
      observer.unobserve(trigger);
    }
    if (timer) { clearTimeout(timer); }
  }

  useEffect(() => {
    if (siteCookie.get(COOKIE_NAME) === null) {
      // タイマーで表示
      setTimer(setTimeout(() => { setRight(RIGHT_POSITION); }, timeout || 1000));
      // スクロール位置で表示
      const trigger = document.getElementById(triggerElementId);
      if (trigger) {
        const obs = new IntersectionObserver(observerCallback);
        setObserver(obs);
        obs.observe(trigger);
      }
    }
    return clear;
  }, []);

  return (
    <TimeSaleView
      href='#'
      style={{ right }}
      onClick={handleClick}
      data-track-element-tapped={trackingName}>
      <img className="icon" src={getImage(presentIcon)} />
      {isLoading ?
        <div style={{ padding: '16px 24px' }}>
          <i className="fas fa-spinner fa-spin" />
        </div> :
        <div>
          <p className="heading">{t.getCoupon({ amount: discountAmount })}</p>
          <p className="caption">{t.timeRemaining()}</p>
          <CountDownView>
            <CountDown cutOffString={cutOffString} />
          </CountDownView>
        </div>
      }
      <TimeSaleClose onClick={handleClose} />
    </TimeSaleView>
  );
};

export default TimeSale;
